import { type AxiosInstance } from 'axios';

import { logError } from '~/common/utils/log';
import GeoComplySingleton, {
  GEO_COMPLY_IP_CHANGE_REASON_CODE,
  type GeoComplyCheckOptions,
  isGeoRestrictionError,
} from '~/utils/geocomply';

export const enum Method {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

export const GeoComplyReasonCode = {
  LOGIN: '1',
  PURCHASE_WAGER: '2',
  PAYMENT_METHOD_CHANGE: '10a',
  DEPOSIT: '11a',
  WITHDRAWAL: '11b',
  IP_CHANGE: GEO_COMPLY_IP_CHANGE_REASON_CODE,
} as const;

export const GeoComplyTokenType = {
  FRESH: 'fresh',
  VALID: 'valid',
} as const;

// Matches the shared `withdrawal_geo_check` analytics contract across web/iOS/Android.
export const GeoCheckOutcome = {
  TOKEN_OBTAINED: 'token_obtained',
  GEO_FAILED: 'geo_failed',
  TOKEN_UNAVAILABLE: 'token_unavailable',
} as const;

export const GeoErrorPolicy = {
  REJECT: 'reject',
  SEND_RESTRICTED_LOCATION_TOKEN: 'send_restricted_location_token',
} as const;

const LOCATION_TOKEN_MIN_VALIDITY_MS = 3 * 60 * 1000;

type GeoCheckResult = {
  outcome: (typeof GeoCheckOutcome)[keyof typeof GeoCheckOutcome];
  blocked: boolean;
};

type LocationTokenRequestOptions = GeoComplyCheckOptions & {
  tokenType?: (typeof GeoComplyTokenType)[keyof typeof GeoComplyTokenType];
  geoErrorPolicy?: (typeof GeoErrorPolicy)[keyof typeof GeoErrorPolicy];
  // Kept as an injected callback so this interceptor stays decoupled from analytics.
  onOutcome?: (result: GeoCheckResult) => void;
};

export type RequiredTokenEndpoint = [Method, string | RegExp, LocationTokenRequestOptions?];

const normalizeUrlPath = (url?: string) => {
  if (!url) return '';

  const [path] = url.split('?');
  return path.replace(/^\/+/, '');
};

export const setLocationInterceptor = (
  instance: AxiosInstance,
  requiredTokenEndpoints: RequiredTokenEndpoint[] = []
) => {
  instance.interceptors.request.use(
    async (config) => {
      if (process.env.STORYBOOK) {
        return config;
      }

      // Try to read the persisted token
      let token = GeoComplySingleton.getPersistedLocationToken();

      // Get fresh token for required endpoints (will still serve the persisted one if it's fresh)
      if (Array.isArray(requiredTokenEndpoints) && requiredTokenEndpoints.length > 0) {
        const requiredTokenEndpoint = requiredTokenEndpoints.find(
          ([method, url]) =>
            method === config.method &&
            (url instanceof RegExp
              ? url.test(normalizeUrlPath(config.url))
              : normalizeUrlPath(url) === normalizeUrlPath(config.url))
        );

        if (requiredTokenEndpoint && !config.skipLocationValidation) {
          const [, , locationCheckOptions] = requiredTokenEndpoint;
          const {
            tokenType = GeoComplyTokenType.VALID,
            geoErrorPolicy = GeoErrorPolicy.REJECT,
            onOutcome,
            ...geoComplyCheckOptions
          } = locationCheckOptions ?? {};
          const {
            errors,
            token: newToken,
            restrictedLocationToken,
          } = await GeoComplySingleton.getValidLocationToken(
            LOCATION_TOKEN_MIN_VALIDITY_MS,
            tokenType === GeoComplyTokenType.FRESH,
            geoComplyCheckOptions
          );

          if (errors) {
            logError('Location token error', errors);

            if (geoErrorPolicy === GeoErrorPolicy.REJECT) {
              return Promise.reject(errors);
            }

            const outcome = errors.some(isGeoRestrictionError)
              ? GeoCheckOutcome.GEO_FAILED
              : GeoCheckOutcome.TOKEN_UNAVAILABLE;

            onOutcome?.({ outcome, blocked: !restrictedLocationToken });

            if (!restrictedLocationToken) {
              return Promise.reject(errors);
            }

            token = restrictedLocationToken;
          } else {
            token = newToken;
            onOutcome?.({ outcome: GeoCheckOutcome.TOKEN_OBTAINED, blocked: false });
          }
        }
      }

      if (token) {
        config.headers.set('location-token-v2', token);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};
